import { FC } from 'react';
import { useLocation } from 'react-router-dom';
import { Box } from '@chakra-ui/react';

import { FeatureName } from '__generated__/GQL';
import useCanCompanyUserAccessFeature from 'common/hooks/useCanCompanyUserAccessFeature';
import CannyChangelogButton from 'components/CannyChangelogButton';
import Logo from 'components/Logo';
import AccountSetupBox from 'features/PostOnboardingAccountSetup/components/AccountSetupBox';
import { usePostOnboardingAccountSetupContext } from 'features/PostOnboardingAccountSetup/context';
import SetupCompletedModal from 'features/PostOnboardingAccountSetup/pages/SetupCompletedModal';
import ReferralBox from 'features/Referral/components/ReferralBox';
import * as referralRoutes from 'features/Referral/routes';
import { ALL_USER_MENU_ITEMS } from 'features/Sidebar/UserMenu/types';

import { useSidebarData } from './hook/useSidebarData';
import NavMenu from './NavMenu';
import * as S from './styles';
import UserMenu from './UserMenu';

interface SidebarProps {
  disableBrowsing: boolean;
}

const Sidebar: FC<SidebarProps> = ({ disableBrowsing }) => {
  const { canAccessFeature } = useCanCompanyUserAccessFeature();
  const location = useLocation();

  const isOnReferralRoute = location.pathname.includes(referralRoutes.root);

  const { loading, remainingReferrerAmount } = useSidebarData();

  const { hasCompletedPostOnboardingSetup } =
    usePostOnboardingAccountSetupContext();

  const shouldShowAccountSetupBox =
    canAccessFeature({ name: FeatureName.Activationonboarding }) &&
    !hasCompletedPostOnboardingSetup;

  const shouldShowReferralBox =
    !isOnReferralRoute &&
    !loading &&
    remainingReferrerAmount &&
    canAccessFeature({ name: FeatureName.Referral });

  return (
    <S.Wrapper>
      <Box paddingX="space-16">
        <UserMenu
          disableBrowsingFor={
            disableBrowsing ? ALL_USER_MENU_ITEMS : new Set([])
          }
        />
      </Box>

      <S.ScrollView>
        <Box paddingX="space-16">
          <NavMenu disableBrowsing={disableBrowsing} />
        </Box>

        <S.Bottom>
          <Box paddingX="space-16">
            {shouldShowAccountSetupBox ? <AccountSetupBox /> : null}

            {shouldShowReferralBox && !shouldShowAccountSetupBox ? (
              <Box marginBottom="space-24">
                <ReferralBox
                  remainingReferrerAmount={remainingReferrerAmount ?? null}
                />
              </Box>
            ) : null}

            {canAccessFeature({ name: FeatureName.Canny }) ? (
              <Box marginBottom="space-24">
                <CannyChangelogButton />
              </Box>
            ) : null}
            <S.BottomLine>
              <Logo width="53px" />
            </S.BottomLine>
          </Box>
        </S.Bottom>
      </S.ScrollView>
      <SetupCompletedModal />
    </S.Wrapper>
  );
};

export default Sidebar;
